<template>
  <b-modal
    :id="modalId"
    :title="titleText"
    header-class="create-wizard-header"
    title-class="create-label"
    footer-class="create-wizard-footer"
    no-close-on-backdrop
    @shown="init"
    @hidden="clearForm"
  >
    <div class="wizard-container">
      <div class="content-wrapper">
        <div class="wizard-form">
          <div class="ncm-row" v-if="isInputReady">
            <label class="ncm-column-left" for="con-name">
              {{ nameText }}
            </label>
            <div class="ncm-column-right m-name">
              <text-input
                id="con-name"
                v-model="appName"
                type="text"
                :focus="true"
                @on-text-input-change="onAppNameInputChanged"
              />
            </div>
          </div>

          <element-description
            :label="descriptionText"
            :outer-container-class="'ncm-row'"
            :label-class="'ncm-column-left'"
            :value-container-class="'ncm-column-right'"
            :elem-description="appDescription"
            @on-element-description-changed="onDeDescriptionChanged"
          ></element-description>

          <div class="ncm-row" v-if="hasTemplates">
            <label class="ncm-column-left">
              {{ selectorText }}
            </label>
            <div class="ncm-column-right">
              <search-box-simple
                class="template-list"
                :list="templates"
                :value="selectedTemplate"
                @on-search-box-item-selected="onTemplateSelected"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button class="modal-btn modal-cancel-btn" @click="clearForm">
        {{ getMessageText(messages.CANCEL) }}
      </button>
      <button
        class="modal-btn modal-save-btn"
        @click="doCreate"
        :disabled="shouldBeDisabled"
      >
        {{ getMessageText(messages.OK) }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import TextInput from "@/components/TextInput";
  import templateServices from "@/services/templates-service";
  import store from "@/store";
  import { MUTATION_NAME } from "@/static/global-variables";
  import ElementDescription from "@/components/ElementDescription";

  export default {
    name: "NewContentFromTemplateModal",
    components: {
      TextInput,
      SearchBoxSimple,
      ElementDescription
    },
    props: {
      appId: {
        default: "",
        type: String
      },
      version: {
        default: 0,
        type: Number
      },
      templates: {
        default: () => [],
        type: Array
      }
    },
    data() {
      return {
        appName: "",
        selectedTemplate: null,
        appDescription: null,
        isInputReady: false
      };
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages"
      }),
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      },
      hasTemplates() {
        return this.templates.length > 0;
      },
      modalId() {
        return "new-cft-" + this.appId;
      },
      selectorText() {
        return this.$t("pageTemplates");
      },
      titleText() {
        return this.$t("newPageFromPageTemplate");
      },
      nameText() {
        return this.$t("pageName");
      },
      shouldBeDisabled() {
        return !this.appName || (this.hasTemplates && !this.selectedTemplate);
      }
    },
    methods: {
      init() {
        this.isInputReady = true;
      },
      clearForm() {
        this.appName = "";
        this.appDescription = null;
        this.isInputReady = false;
        this.$bvModal.hide(this.modalId);
      },
      onTemplateSelected(ctx) {
        if (!ctx || ctx.isEmpty) {
          this.selectedTemplate = null;
        } else {
          this.selectedTemplate = ctx;
        }
      },
      displayErrorMessage(message) {
        this.$bvToast.toast(message, {
          solid: true,
          toaster: "b-toaster-bottom-center",
          variant: "danger"
        });
      },
      errorHandler(error) {
        const component = this;
        console.error(error);
        this.displayErrorMessage(
          component.getMessageText(component.messages.SAVE_ERROR)
        );
      },
      navigateToEditPage(app) {
        this.$router.push({
          name: "DataPalette",
          params: {
            appId: app.uuid,
            appName: app.name,
            type: app.type,
            testRecord: app.testRecord
          }
        });
      },
      doCreate() {
        const appId = this.hasTemplates
          ? this.selectedTemplate.uuid
          : this.appId;
        const version = this.hasTemplates
          ? this.selectedTemplate.version
          : this.version;
        const params = {
          name: this.appName,
          description: this.appDescription,
          sourceTemplateUuid: appId,
          sourceTemplateVersion: version,
          isTemplate: false
        };

        templateServices
          .createContentFromTemplate(params)
          .then(resp => {
            if (!resp) {
              this.displayErrorMessage(
                this.getMessageText(this.messages.SAVE_ERROR)
              );
              return;
            }

            const createdApp = resp;
            store.commit(MUTATION_NAME.ADD_APP, createdApp);
            this.navigateToEditPage(createdApp);
          }, this.errorHandler)
          .catch(this.errorHandler);
        this.clearForm();
      },
      onAppNameInputChanged(val) {
        this.appName = val;
      },
      onDeDescriptionChanged(input) {
        this.appDescription = input;
      }
    }
  };
</script>

<style scoped>
  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid #dddbda;
    background-color: #fff;
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE);
  }
</style>
